import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { NodeBadge } from "./NodeBadge";
import {
	LayoutDashboard,
	BookMarked,
	BookOpen,
	MessageSquare,
	NotebookPen,
	ListChecks,
	Target,
	DollarSign,
	Search,
	ShieldCheck,
	HeartPulse,
	User,
	FileKey,
	Bot,
	CandlestickChart,
	TrendingUp,
	Sparkles,
	Users,
	BookUser,
	FolderKanban,
	Briefcase,
	Share2,
	Settings,
	FileText,
	PanelTop,
	Puzzle,
	AlertTriangle,
	Dna,
	ScrollText,
	Inbox,
	SlidersHorizontal,
	KeyRound,
	Brain,
	Heart,
	Moon,
	Sun,
	Circle,
	Cloud,
	Cpu,
	ChevronDown,
	ChevronRight,
	LayoutGrid,
	FolderTree,
	Layers,
	Film,
	AudioLines,
	Receipt,
	Microscope,
	Trello,
	Database,
	HardDriveDownload,
	Timer,
	Clock,
	CalendarDays,
	Network,
	type LucideIcon,
	Repeat,
	Plug,
	Star,
	MoreHorizontal,
} from "lucide-react";
import { useChatStore } from "../../stores/chat";
import { useThemeStore } from "../../stores/theme";
import { useSidebarProfileStore } from "../../stores/sidebarProfile";
import { type Favorite, favoritePath, useFavoritesStore } from "../../stores/favorites";
import { AgntLogo } from "./AgntLogo";
import { RunWhereMark } from "./RunWhereMark";
import { ActionMenu, type ActionMenuItem } from "../common/ActionMenu";
import { FilesPanel } from "./FilesPanel";
import { SystemLoadIndicator } from "./SystemLoadIndicator";
import styles from "./Sidebar.module.css";

export interface NavItem {
	path: string;
	labelKey: string;
	/** English fallback used when the locale is missing the key. */
	labelFallback: string;
	icon: LucideIcon;
	/**
	 * Optional theme-aware tint applied as a CSS class on the icon.
	 * Maps to `--icon-{tint}` custom properties defined per theme in
	 * globals.css. The zinc theme collapses every tint to neutral gray
	 * to preserve its monochrome aesthetic. NEVER use inline hex colors
	 * here — they don't theme-swap.
	 */
	iconTint?: "journal" | "wiki" | "graph" | "agents" | "heartbeat" | "logs" | "decisions";
}
export interface NavGroup {
	/** Also the group's collapse key in More — keep it stable. */
	labelKey: string;
	labelFallback: string;
	items: NavItem[];
}

/** Every page, in the groups the More view shows (System last). The command
 *  palette reads this too, so a page added here is also searchable. */
export const NAV_GROUPS: NavGroup[] = [
	{
		labelKey: "",
		labelFallback: "",
		items: [
			{ path: "/dashboard", labelKey: "nav.dashboard", labelFallback: "Dashboard", icon: LayoutDashboard },
			{ path: "/chat", labelKey: "nav.chat", labelFallback: "Chat", icon: MessageSquare },
			{ path: "/routines", labelKey: "nav.routines", labelFallback: "Routines", icon: Repeat },
			{ path: "/connections", labelKey: "nav.integrations", labelFallback: "Integrations", icon: Plug },
			{ path: "/canvas", labelKey: "nav.canvas", labelFallback: "Canvas", icon: PanelTop },
		],
	},
	{
		labelKey: "nav.work",
		labelFallback: "Life & Work",
		items: [
			{ path: "/journal", labelKey: "nav.journal", labelFallback: "Journal", icon: NotebookPen, iconTint: "journal" },
			{ path: "/goals", labelKey: "nav.goals", labelFallback: "Goals", icon: Target },
			{ path: "/projects", labelKey: "nav.projects", labelFallback: "Projects", icon: FolderKanban },
			{ path: "/tasks", labelKey: "nav.tasks", labelFallback: "Tasks", icon: ListChecks },
			{ path: "/focus", labelKey: "nav.focus", labelFallback: "Focus", icon: Timer },
			{ path: "/time", labelKey: "nav.time", labelFallback: "Time", icon: Clock },
			{ path: "/calendar", labelKey: "nav.calendar", labelFallback: "Calendar", icon: CalendarDays },
			{ path: "/contacts", labelKey: "nav.contacts", labelFallback: "Contacts", icon: BookUser },
			{ path: "/kanban", labelKey: "nav.automationBoards", labelFallback: "Automation boards", icon: Trello },
			{ path: "/collections", labelKey: "nav.collections", labelFallback: "Collections", icon: Database },
			{ path: "/imports", labelKey: "nav.imports", labelFallback: "Import", icon: HardDriveDownload },
			{ path: "/finance", labelKey: "nav.finance", labelFallback: "Finance", icon: Receipt },
			{ path: "/investor", labelKey: "nav.investor", labelFallback: "Investor", icon: CandlestickChart },
			{ path: "/career", labelKey: "nav.career", labelFallback: "Career", icon: Briefcase },
		],
	},
	{
		labelKey: "nav.knowledge",
		labelFallback: "Knowledge",
		items: [
			{ path: "/wiki", labelKey: "nav.wiki", labelFallback: "Wiki", icon: BookOpen, iconTint: "wiki" },
			{ path: "/research", labelKey: "nav.research", labelFallback: "Research", icon: Microscope },
			{ path: "/pipeline", labelKey: "nav.pipeline", labelFallback: "Knowledge Ingestion", icon: Layers },
			{ path: "/videos", labelKey: "nav.videos", labelFallback: "Videos", icon: Film },
			{ path: "/media", labelKey: "nav.media", labelFallback: "Media", icon: AudioLines },
			{ path: "/references", labelKey: "nav.references", labelFallback: "References", icon: BookMarked },
			{ path: "/graph", labelKey: "nav.graph", labelFallback: "Graph", icon: Share2, iconTint: "graph" },
			// /graph-next hidden from sidebar — route still mounted in App.tsx so
			// it's reachable via direct URL while v2 is parked.
			{ path: "/ontology", labelKey: "nav.ontology", labelFallback: "Ontology", icon: Network },
			{ path: "/memory", labelKey: "nav.memory", labelFallback: "Memory", icon: Brain },
		],
	},
	{
		labelKey: "nav.identity",
		labelFallback: "Identity",
		items: [
			{ path: "/control/profile", labelKey: "nav.profile", labelFallback: "My Profile", icon: User },
			{ path: "/control/contract", labelKey: "nav.contract", labelFallback: "Contract", icon: FileKey },
			{ path: "/control/agent", labelKey: "nav.agent", labelFallback: "Agent Soul", icon: Bot },
			{ path: "/control/relationship", labelKey: "nav.relationship", labelFallback: "Relationship", icon: Heart },
			{ path: "/control/trust", labelKey: "nav.trust", labelFallback: "Trust", icon: TrendingUp },
			{ path: "/skills", labelKey: "nav.skills", labelFallback: "Skills", icon: Sparkles },
			{ path: "/agents", labelKey: "nav.agents", labelFallback: "Agents", icon: Users, iconTint: "agents" },
		],
	},
	{
		labelKey: "nav.system",
		labelFallback: "System",
		items: [
			{ path: "/llm", labelKey: "nav.llm", labelFallback: "LLM", icon: Cpu },
			{ path: "/plugins", labelKey: "nav.plugins", labelFallback: "Plugin system", icon: Puzzle },
			{ path: "/costs", labelKey: "nav.costs", labelFallback: "Costs", icon: DollarSign },
			{ path: "/exposure", labelKey: "nav.exposure", labelFallback: "Cloud Exposure", icon: Cloud },
			{ path: "/heartbeat", labelKey: "nav.heartbeat", labelFallback: "Heartbeat", icon: HeartPulse, iconTint: "heartbeat" },
			{ path: "/logs", labelKey: "nav.errorLogs", labelFallback: "Error Logs", icon: AlertTriangle, iconTint: "logs" },
			{ path: "/decisions", labelKey: "nav.decisions", labelFallback: "Decisions", icon: ScrollText, iconTint: "decisions" },
			{ path: "/proposals", labelKey: "nav.proposals", labelFallback: "Proposals", icon: Inbox, iconTint: "decisions" },
			{ path: "/evolution", labelKey: "nav.evolution", labelFallback: "Evolution", icon: Dna, iconTint: "decisions" },
			{ path: "/admin", labelKey: "nav.admin", labelFallback: "Admin", icon: SlidersHorizontal },
			// Deep-link into Settings → Integrations → MCP Tokens. The hash
			// drives the SettingsPage tab + scrollspy to the right section.
			{ path: "/settings#settings-mcp-tokens", labelKey: "nav.mcpTokens", labelFallback: "MCP Tokens", icon: KeyRound },
			{ path: "/guardian", labelKey: "nav.guardian", labelFallback: "Guardian", icon: ShieldCheck },
			{ path: "/docs", labelKey: "nav.docs", labelFallback: "Docs", icon: FileText },
			{ path: "/settings", labelKey: "nav.settings", labelFallback: "Settings", icon: Settings },
		],
	},
];

/** Simple profile (A4, 2026-09-03): the most-used surfaces only — hand work to
 *  an agent and see what needs you (owner, 2026-10-03: the Work group that sat
 *  here went to More). New devices start here; the More pill switches to the
 *  whole workspace, and Favorites sit above both. Same NavItem shape so
 *  badges/active state are shared with the full nav. */
export const SIMPLE_GROUPS: NavGroup[] = [
	{
		labelKey: "",
		labelFallback: "",
		items: [
			{ path: "/chat", labelKey: "nav.chat", labelFallback: "Chat", icon: MessageSquare },
			{ path: "/agents", labelKey: "nav.bots", labelFallback: "Agents", icon: Users, iconTint: "agents" },
			{ path: "/routines", labelKey: "nav.routines", labelFallback: "Routines", icon: Repeat },
			// 2026-09-06: "Plugins" = the sites and services an agent uses AS YOU (Grok's word).
			{ path: "/connections", labelKey: "nav.integrations", labelFallback: "Integrations", icon: Plug },
			{ path: "/inbox", labelKey: "nav.inbox", labelFallback: "Inbox", icon: Inbox, iconTint: "decisions" },
			{ path: "/settings", labelKey: "nav.settings", labelFallback: "Settings", icon: Settings },
		],
	},
];

/**
 * The nav item a page belongs to: the longest nav route the pathname sits on
 * (anchored items are sections of another item's page, so they never claim).
 * Names a favorite's module and supplies its icon.
 */
export function navItemFor(pathname: string): NavItem | undefined {
	let best: NavItem | undefined;
	let bestLen = -1;
	for (const item of [...NAV_GROUPS, ...SIMPLE_GROUPS].flatMap((g) => g.items)) {
		const own = splitNavPath(item.path);
		if (own.hash) continue;
		const on = pathname === own.pathname || pathname.startsWith(`${own.pathname}/`);
		if (on && own.pathname.length > bestLen) {
			best = item;
			bestLen = own.pathname.length;
		}
	}
	return best;
}

/**
 * A chat conversation's title, for a favorite that is one. A conversation's
 * page has no heading to read and its address is an opaque id, so its name is
 * only in the chat store — looked up when starred AND when listed, so a
 * renamed conversation shows its new name.
 */
export function conversationTitle(
	pathname: string,
	conversations: readonly { id: string; title: string }[],
): string | undefined {
	const match = /^\/chat\/([^/?#]+)/.exec(pathname);
	if (!match) return undefined;
	let id = match[1] ?? "";
	try {
		id = decodeURIComponent(id);
	} catch {
		/* not percent-encoded as expected — compare it as it is */
	}
	return conversations.find((c) => c.id === id)?.title.trim() || undefined;
}

/**
 * What to call a page when it is starred. A module's own page is its nav name.
 * Anything deeper is named by the most specific heading on the page (a wiki
 * article's title, a project's name), then by the tab that is open when the
 * heading is only the module's name ("Ledger" on /finance/ledger, as the tab
 * reads), or failing both by the module plus the part of the address that
 * picks the record ("Finance · merchants").
 */
export function favoriteLabel(
	location: { pathname: string; search: string },
	moduleName: (item: NavItem) => string,
	root: ParentNode | null = typeof document === "undefined" ? null : document.getElementById("main-content"),
): string {
	const item = navItemFor(location.pathname);
	const module = item ? moduleName(item) : "";
	const modulePath = item ? splitNavPath(item.path).pathname : "";
	if (item && location.pathname === modulePath && !location.search) return module;
	const heading = (root ? Array.from(root.querySelectorAll("h1")) : [])
		.map((h) => (h.textContent ?? "").replace(/\s+/g, " ").trim())
		.filter((text) => text && text !== module)
		.pop();
	if (heading) return heading.length > 80 ? `${heading.slice(0, 79)}…` : heading;
	const tab = (root?.querySelector('[role="tab"][aria-selected="true"]')?.textContent ?? "")
		.replace(/\s+/g, " ")
		.trim();
	if (tab && tab !== module) return tab.length > 80 ? `${tab.slice(0, 79)}…` : tab;
	const rest = location.pathname.slice(modulePath.length).split("/").filter(Boolean).pop() ?? "";
	let detail = rest;
	try {
		detail = decodeURIComponent(rest);
	} catch {
		/* not percent-encoded as expected — keep it as typed */
	}
	if (!detail) detail = new URLSearchParams(location.search).values().next().value ?? "";
	return [module, detail].filter(Boolean).join(" · ") || location.pathname;
}

/** A nav path's route and its section anchor (`/settings#settings-mcp-tokens`). */
function splitNavPath(path: string): { pathname: string; hash: string } {
	const at = path.indexOf("#");
	return at < 0 ? { pathname: path, hash: "" } : { pathname: path.slice(0, at), hash: path.slice(at) };
}

/**
 * Whether a nav item names the page being shown.
 *
 * A plain item matches its route and anything nested under it; the
 * trailing-slash boundary stops "/journal" from matching "/journal-archive"
 * (which a bare startsWith would). An item that carries a section anchor
 * ("/settings#settings-mcp-tokens") matches only that route AND that anchor —
 * comparing the pathname with a path containing "#" never matched, so it was
 * never highlighted. When an anchored item matches, the plain item for the
 * same route ("Settings") stands down, so exactly one item is current; with
 * any other anchor (or none) the plain item is the current one.
 * `items` is what the sidebar shows right now: an anchored item that is not
 * visible (Simple profile, Main mode) claims nothing.
 */
export function isNavItemActive(
	path: string,
	items: readonly { path: string }[],
	at: { pathname: string; hash: string },
): boolean {
	const own = splitNavPath(path);
	if (own.hash) return at.pathname === own.pathname && at.hash === own.hash;
	const onRoute = at.pathname === own.pathname || at.pathname.startsWith(`${own.pathname}/`);
	if (!onRoute || !at.hash) return onRoute;
	return !items.some((other) => {
		const o = splitNavPath(other.path);
		return o.hash !== "" && o.hash === at.hash && o.pathname === at.pathname;
	});
}

const THEME_ICONS = { dark: Moon, light: Sun, zinc: Circle } as const;
const THEME_ORDER = ["dark", "light", "zinc"] as const;

/**
 * Three views (owner, 2026-10-03): Simple, More, Files. "main" is the nav
 * list (Simple or More, by profile); "files" swaps it for the files panel.
 * System used to be a fourth view of its own; it is now More's last group, so
 * a stored "system" reads as "main" and that device lands in More.
 */
type SidebarMode = "main" | "files";
const MODE_STORAGE_KEY = "agnt-sidebar-mode";
const MODE_DEFAULT: SidebarMode = "main";

function readMode(): SidebarMode {
	try {
		const v = localStorage.getItem(MODE_STORAGE_KEY);
		if (v === "files") return v;
		if (v === "main" || v === "system") return "main";
	} catch {
		/* localStorage unavailable (private mode, etc.) — fall through */
	}
	return MODE_DEFAULT;
}

/** More's groups the user has folded away, by `labelKey`. Per device, like
 *  the mode. All groups start open: folding is the user's choice. */
const COLLAPSED_STORAGE_KEY = "agnt-sidebar-collapsed";

function readCollapsed(): Set<string> {
	try {
		const raw = JSON.parse(localStorage.getItem(COLLAPSED_STORAGE_KEY) ?? "[]");
		if (Array.isArray(raw)) return new Set(raw.filter((k): k is string => typeof k === "string"));
	} catch {
		/* unavailable or not JSON — every group open */
	}
	return new Set();
}

function writeCollapsed(keys: Set<string>): void {
	try {
		localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...keys]));
	} catch {
		/* ignore — the fold still applies this session */
	}
}

/** Whether `pathname` is a nav item's route or nested under it. */
function onItemRoute(item: { path: string }, pathname: string): boolean {
	const own = splitNavPath(item.path).pathname;
	return pathname === own || pathname.startsWith(`${own}/`);
}

function writeMode(mode: SidebarMode): void {
	try {
		localStorage.setItem(MODE_STORAGE_KEY, mode);
	} catch {
		/* ignore */
	}
}

/** One badge count per nav destination. Polls every 30s.
 *
 * ONE request, from ONE server-side aggregator. This used to be five parallel
 * fetches assembled in the browser, and two of the numbers were wrong:
 * `/api/memory/dream-review?limit=50` was counted as `queue.length`, so 60
 * pending beliefs rendered as "50" — a Rule 20 silent cap in the one place a
 * user looks to decide whether anything needs them. The counts now come from
 * the same authorities the briefing and the agent read.
 *
 * `exact: false` means the true total is unknowable (a graph-backed queue
 * came back at exactly its read limit); the badge renders "N+" rather than a
 * confident number. An absent key means no badge — which is different from
 * zero, and deliberately so: a disabled or unwired source has said nothing
 * about its queue.
 */
interface BadgeCount { count: number; exact: boolean }

function usePendingCounts() {
	const [counts, setCounts] = useState<Record<string, BadgeCount>>({});
	useEffect(() => {
		let alive = true;
		const poll = async () => {
			try {
				const res = await fetch("/api/nav-badges");
				if (!res.ok) return;
				const body = await res.json();
				if (!alive) return;
				setCounts(body.enabled === false ? {} : (body.badges ?? {}));
			} catch { /* polling — silent on transient failure, retries every 30s */ }
		};
		poll();
		const id = setInterval(poll, 30000);
		return () => { alive = false; clearInterval(id); };
	}, []);
	return counts;
}

/** "15", or "50+" when the true total is a floor (Rule 20). */
function formatBadge(b: BadgeCount | undefined): string | null {
	if (!b || !b.count) return null;
	return b.exact ? String(b.count) : `${b.count}+`;
}

/** The badge key for a nav path: the route itself, which is how the server
 *  keys them. /bots (the roster's legacy address) shares the agents badge. */
function badgeKey(path: string): string {
	return path === "/bots" ? "agents" : path.replace("/", "");
}

/**
 * One badge for items the user cannot see right now (a folded group, or the
 * More pill while Simple or Files is showing). Each key counts once — Settings
 * sits in two groups — and if ANY part is a floor the sum is a floor too, or
 * the roll-up would state a total more precisely than its parts.
 */
function rollUpBadge(items: readonly { path: string }[], counts: Record<string, BadgeCount>): string | null {
	const parts = [...new Set(items.map((i) => badgeKey(i.path)))]
		.map((k) => counts[k])
		.filter((b): b is BadgeCount => Boolean(b));
	const total = parts.reduce((n, b) => n + b.count, 0);
	return formatBadge({ count: total, exact: parts.every((b) => b.exact) });
}

export function Sidebar() {
	const { t } = useTranslation();
	const { loadConversations } = useChatStore();
	const conversations = useChatStore((s) => s.conversations);
	const theme = useThemeStore((s) => s.theme);
	const setTheme = useThemeStore((s) => s.setTheme);
	const navigate = useNavigate();
	const location = useLocation();
	const [version, setVersion] = useState("");
	const [vaultName, setVaultName] = useState("");
	const pendingCounts = usePendingCounts();
	const [mode, setModeState] = useState<SidebarMode>(() => readMode());
	const profile = useSidebarProfileStore((s) => s.profile);
	const setProfile = useSidebarProfileStore((s) => s.setProfile);
	const simpleActive = profile === "simple" && mode === "main";
	const moreActive = profile !== "simple" && mode === "main";
	const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);

	const setMode = (next: SidebarMode) => {
		setModeState(next);
		writeMode(next);
	};

	const toggleGroup = (key: string) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			writeCollapsed(next);
			return next;
		});
	};

	const favorites = useFavoritesStore((s) => s.favorites);
	const toggleFavorite = useFavoritesStore((s) => s.toggle);
	const here = favoritePath(location);
	const moreItems = NAV_GROUPS.flatMap((g) => g.items);
	const simpleItems = SIMPLE_GROUPS.flatMap((g) => g.items);

	// The list this view already draws. Files draws none of it — the tree
	// replaces the nav — so a starred tool has to be its own row there or it
	// is not in the panel at all. A tool the list already shows is not repeated.
	const listGroups = mode === "files" ? [] : simpleActive ? SIMPLE_GROUPS : NAV_GROUPS;
	const onScreenPaths = new Set(listGroups.flatMap((g) => g.items).map((i) => i.path));
	// What Simple would pin, computed in every view. The Simple pill decides
	// whether to stay on this page at the click, before the profile re-renders,
	// so it cannot read the list Simple is about to draw.
	const simplePaths = new Set(simpleItems.map((i) => i.path));
	const simplePinnedTools = favorites
		.map((f) => moreItems.find((i) => i.path === f.path))
		.filter((i): i is NavItem => i !== undefined && !simplePaths.has(i.path));
	const pinnedTools = favorites
		.map((f) => moreItems.find((i) => i.path === f.path))
		.filter((i): i is NavItem => i !== undefined && !onScreenPaths.has(i.path));
	const pinnedGroup: NavGroup = { labelKey: "", labelFallback: "", items: pinnedTools };
	// Starred PAGES are rows of the panel in every view (owner, 2026-10-24:
	// they were missing from More and Files, where only the ⋯ menu held them,
	// and in More that menu sits under a list long enough to scroll them out
	// of sight). A favorite whose address is a tool's own path is a tool.
	const toolPaths = new Set([...moreItems, ...simpleItems].map((i) => i.path));
	const pinnedPages = favorites.filter((f) => !toolPaths.has(f.path));
	const favoritePaths = new Set(favorites.map((f) => f.path));
	// A page row is current only at its exact address, and while it is, the
	// module row it sits under stands down, so exactly one row is current.
	const pageHere = pinnedPages.some((f) => f.path === here);

	// Pinned tools count as shown, including in Files, so the More pill does
	// not roll up a badge for a row that is already in the panel.
	const visibleItems = [...listGroups.flatMap((g) => g.items), ...pinnedTools];
	const shownPaths = new Set(visibleItems.map((i) => i.path));
	const moreRollUp = moreActive ? null : rollUpBadge(moreItems.filter((i) => !shownPaths.has(i.path)), pendingCounts);

	useEffect(() => {
		loadConversations();
		fetch("/api/health").then(r => r.json()).then(d => setVersion(d.version || "")).catch(() => {});
		fetch("/api/sync").then(r => r.json()).then(d => {
			// Prefer external_path (the user's chosen vault root). Fall back to
			// the parent of vault_path (which points at <root>/agntspace).
			const raw: string = d.external_path || d.vault_path || "";
			if (!raw) return;
			const segments = raw.replace(/[\\/]+$/, "").split(/[\\/]/).filter(Boolean);
			let name = segments[segments.length - 1] || "";
			if (name === "agntspace" && segments.length >= 2) {
				name = segments[segments.length - 2] ?? name;
			}
			if (name) setVaultName(name);
		}).catch(() => {});
	}, [loadConversations]);

	const hasPinned = pinnedTools.length > 0 || pinnedPages.length > 0;

	function renderNavGroup(group: NavGroup) {
		const groupLabel = t(group.labelKey, group.labelFallback);
		// More's labelled groups fold; Simple is short enough not to.
		const foldable = moreActive && Boolean(group.labelKey);
		const folded = foldable && collapsed.has(group.labelKey);
		const itemsId = `sidebar-group-${group.labelKey.replace(/[^a-z0-9]+/gi, "-")}`;
		// A folded group still shows the page you are on, and a tool you
		// starred, so folding never hides a favorite.
		const hiddenItems = folded
			? group.items.filter((i) => !isNavItemActive(i.path, visibleItems, location) && !favoritePaths.has(i.path))
			: [];
		const foldedBadge = folded ? rollUpBadge(hiddenItems, pendingCounts) : null;
		const pinned = group === pinnedGroup;
		return (
			<div
				key={pinned ? "_pinned" : group.labelKey || "_top"}
				className={`${styles.navGroup} ${pinned ? styles.pinnedTools : ""}`}
				data-pinned-tools={pinned || undefined}
			>
				{group.labelKey && (foldable ? (
					<div className={`${styles.groupLabel} ${styles.groupLabelFoldable}`}>
						<button
							type="button"
							className={styles.groupToggle}
							aria-expanded={!folded}
							aria-controls={itemsId}
							onClick={() => toggleGroup(group.labelKey)}
						>
							{folded
								? <ChevronRight size={14} className={styles.groupChevron} aria-hidden="true" />
								: <ChevronDown size={14} className={styles.groupChevron} aria-hidden="true" />}
							<span>{groupLabel}</span>
							{foldedBadge && <span className={styles.pendingBadge}>{foldedBadge}</span>}
						</button>
					</div>
				) : (
					<div className={styles.groupLabel}>{groupLabel}</div>
				))}
				<div id={group.labelKey ? itemsId : undefined}>
				{group.items.map((item) => {
					const isActive = !pageHere && isNavItemActive(item.path, visibleItems, location);
					const Icon = item.icon;
					const label = formatBadge(pendingCounts[badgeKey(item.path)]);
					const toolName = t(item.labelKey, item.labelFallback);
					const isFavorite = favorites.some((f) => f.path === item.path);
					return (
						<div
							key={item.path}
							className={`${styles.navRow} ${folded && !isActive && !favoritePaths.has(item.path) ? styles.foldedItem : ""}`}
							data-nav-row
						>
						<button
							type="button"
							className={`${styles.navItem} ${isActive ? styles.active : ""}`}
							onClick={() => navigate(item.path)}
							aria-current={isActive ? "page" : undefined}
							/* In rail mode the label is hidden, so the name has to
							   survive as a tooltip — an icon nobody can name is not
							   navigation. The accessible name comes from the span,
							   which stays in the tree either way. */
							title={t(item.labelKey, item.labelFallback)}
						>
							<Icon
								size={20}
								className={`${styles.icon} ${
									item.iconTint ? styles[`tint_${item.iconTint}`] : ""
								}`}
								aria-hidden="true"
							/>
							<span className={styles.navLabel}>
								{t(item.labelKey, item.labelFallback)}
							</span>
							{label && <span className={styles.pendingBadge}>{label}</span>}
						</button>
						{/* Star the TOOL itself — not whatever page of it is open
						    (that is the top-bar star). A starred tool is pinned
						    into Simple; on a pinned row the same star unpins it. */}
						{(moreActive || pinned) && (
							<button
								type="button"
								className={`${styles.toolStar} ${isFavorite ? styles.toolStarOn : ""}`}
								aria-pressed={isFavorite}
								aria-label={isFavorite
									? t("nav.favoriteToolRemove", { tool: toolName, defaultValue: "Unpin {{tool}} from Simple" })
									: t("nav.favoriteToolAdd", { tool: toolName, defaultValue: "Pin {{tool}} to Simple" })}
								title={isFavorite
									? t("nav.favoriteToolRemove", { tool: toolName, defaultValue: "Unpin {{tool}} from Simple" })
									: t("nav.favoriteToolAdd", { tool: toolName, defaultValue: "Pin {{tool}} to Simple" })}
								onClick={() => toggleFavorite({ path: item.path, label: toolName })}
							>
								<Star size={14} aria-hidden="true" fill={isFavorite ? "currentColor" : "none"} />
							</button>
						)}
						</div>
					);
				})}
				{pinned && pinnedPages.map((page) => {
					const pathname = page.path.split("?")[0] ?? page.path;
					const Icon = navItemFor(pathname)?.icon ?? Star;
					const name = conversationTitle(pathname, conversations) ?? page.label;
					const isActive = page.path === here;
					const unstar = t("nav.favoritePageRowRemove", { page: name, defaultValue: "Remove {{page}} from favorites" });
					return (
						<div key={page.path} className={styles.navRow} data-nav-row data-favorite-page>
							<button
								type="button"
								className={`${styles.navItem} ${isActive ? styles.active : ""}`}
								onClick={() => navigate(page.path)}
								aria-current={isActive ? "page" : undefined}
								title={name}
							>
								<Icon size={20} className={styles.icon} aria-hidden="true" />
								<span className={styles.navLabel}>{name}</span>
							</button>
							<button
								type="button"
								className={`${styles.toolStar} ${styles.toolStarOn}`}
								aria-pressed={true}
								aria-label={unstar}
								title={unstar}
								onClick={() => toggleFavorite(page)}
							>
								<Star size={14} aria-hidden="true" fill="currentColor" />
							</button>
						</div>
					);
				})}
				</div>
			</div>
		);
	}

	return (
		<aside className={styles.sidebar}>
			<div className={styles.header}>
				<button
					type="button"
					className={styles.logoBtn}
					onClick={() => navigate("/dashboard")}
				>
					<AgntLogo size={38} />
					<span className={styles.logoWrap}>
						<span className={styles.logoText}><span className={styles.logoGreen}>AGNT</span><span className={styles.logoDark}>SPACE</span></span>
						<RunWhereMark />
					</span>
				</button>
			</div>

			<SystemLoadIndicator />

			<div className={styles.searchSection}>
				<button
					type="button"
					className={`${styles.searchBtn} ${
						location.pathname === "/search" || location.pathname.startsWith("/search/")
							? styles.active
							: ""
					}`}
					onClick={() => navigate("/search")}
				>
					<Search size={18} />
					<span>{t("nav.searchVault", "Search vault")}</span>
				</button>
			</div>

			<div className={styles.modeSwitcher} role="tablist" aria-label={t("nav.mainNavigation", "Main navigation")}>
				<button
					type="button"
					role="tab"
					aria-selected={simpleActive}
					className={`${styles.modePill} ${simpleActive ? styles.modePillActive : ""}`}
					onClick={() => {
						setProfile("simple");
						setMode("main");
						// Stay on a Simple page or a favorite; otherwise land on Chat.
						if (![...simpleItems, ...simplePinnedTools].some((i) => onItemRoute(i, location.pathname)) && !favorites.some((f) => f.path === here)) {
							navigate("/chat");
						}
					}}
					title={t("nav.modeSimple", "Simple")}
					data-sidebar-profile="simple"
				>
					<Sparkles size={14} />
					<span>{t("nav.modeSimple", "Simple")}</span>
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={moreActive}
					className={`${styles.modePill} ${moreActive ? styles.modePillActive : ""}`}
					onClick={() => {
						setProfile("full");
						setMode("main");
						// Stay on a page More lists (Chat, Settings, …); from
						// anywhere else land on the dashboard, its home page.
						// Symmetric with Simple → /chat and Files → /files.
						if (!moreItems.some((i) => onItemRoute(i, location.pathname))) {
							navigate("/dashboard");
						}
					}}
					title={t("nav.modeMore", "More")}
					data-sidebar-profile="full"
				>
					<LayoutGrid size={14} />
					<span>{t("nav.modeMore", "More")}</span>
					{/* While Simple or Files is showing, More's waiting work is
					    out of sight — roll its badges up onto the pill. */}
					{moreRollUp && <span className={styles.pendingBadge}>{moreRollUp}</span>}
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={mode === "files"}
					className={`${styles.modePill} ${mode === "files" ? styles.modePillActive : ""}`}
					onClick={() => {
						setProfile("full");
						setMode("files");
						// Unload whatever page is in the main content area and
						// bring up the preview canvas — Files mode is the only
						// one that owns a dedicated workspace.
						if (location.pathname !== "/files") {
							navigate("/files");
						}
					}}
					title={t("nav.modeFiles", "Files")}
				>
					<FolderTree size={14} />
					<span>{t("nav.modeFiles", "Files")}</span>
				</button>
			</div>

			{mode === "files" && <FilesPanel />}
			{(mode !== "files" || hasPinned) && (
			<nav
				className={`${mode === "files" ? styles.pinnedSlot : styles.panelBody} ${moreActive || hasPinned ? styles.navStars : ""}`}
				aria-label={t("nav.mainNavigation", "Main navigation")}
			>
				{mode !== "files" && (
					<div className={styles.nav} data-nav-scroll>
						{listGroups.map((group) => renderNavGroup(group))}
					</div>
				)}
				{hasPinned && (
					<div className={mode === "files" ? undefined : styles.pinnedSlot}>
						{renderNavGroup(pinnedGroup)}
					</div>
				)}
			</nav>
			)}

			{/* Starred pages are rows in the slot above, in every view, so the
			    menu would only repeat them. It stays while there are none, for
			    its how-to hint. */}
			{!pinnedPages.length && <FavoritesMenu favorites={favorites} onOpen={(path) => navigate(path)} />}

			{/* N3: where this UI comes from, when it is not this machine. */}
			<NodeBadge />
			<div className={styles.sidebarFooter}>
				<button
					type="button"
					className={styles.themeBtn}
					onClick={() => {
						const idx = THEME_ORDER.indexOf(theme as typeof THEME_ORDER[number]);
						const next = THEME_ORDER[((idx < 0 ? 0 : idx) + 1) % THEME_ORDER.length] ?? "dark";
						setTheme(next);
					}}
					aria-label={`Switch theme (current: ${theme})`}
					title={`Theme: ${theme}`}
				>
					{(() => { const Icon = THEME_ICONS[theme as keyof typeof THEME_ICONS] ?? Moon; return <Icon size={14} />; })()}
					<span className={styles.themeBtnLabel}>{theme}</span>
				</button>
				{vaultName && (
					<span className={styles.versionTag} title={t("nav.activeVault", "Active vault")}>
						{vaultName}
					</span>
				)}
				{/* The version reads as a footnote, not as part of the name — it
				    belongs beside the vault it describes (owner, 2026-09-12).
				    Hidden in rail mode with the rest of the footer's text. */}
				{version && (
					<span className={styles.version} title={t("nav.version", "Version")}>
						v{version}
					</span>
				)}
			</div>
		</aside>
	);
}

/**
 * The empty-state hint for favorite pages (owner, 2026-10-03): ONE row below
 * the nav, with a `⋯` menu. Once a page is starred it is a row of the panel
 * in Simple, More, and Files (2026-10-24), and this menu stays hidden so it
 * does not repeat those rows. A favorite is an address; one that is a tool's
 * own address is a pinned TOOL, so it is not repeated here. Every other
 * favorite is a page starred with the top-bar star, listed under the label it
 * was starred with — a conversation under its live title. The menu is the
 * shared ActionMenu; this only shapes its trigger like a sidebar row and
 * opens it upward.
 */
function FavoritesMenu({ favorites, onOpen }: { favorites: Favorite[]; onOpen: (path: string) => void }) {
	const { t } = useTranslation();
	const conversations = useChatStore((s) => s.conversations);
	const toolPaths = new Set([...NAV_GROUPS, ...SIMPLE_GROUPS].flatMap((g) => g.items).map((i) => i.path));
	const pages: ActionMenuItem[] = favorites
		.filter((favorite) => !toolPaths.has(favorite.path))
		.map((favorite) => {
			const pathname = favorite.path.split("?")[0] ?? favorite.path;
			const Icon = navItemFor(pathname)?.icon ?? Star;
			const label = conversationTitle(pathname, conversations) ?? favorite.label;
			return { id: favorite.path, label, title: label, icon: <Icon size={15} aria-hidden="true" />, onSelect: () => onOpen(favorite.path) };
		});
	const items: ActionMenuItem[] = pages.length
		? pages
		: [{
				id: "favorites-empty",
				label: t("nav.favoritesEmpty", "Star any page with ☆ at the top to keep it here"),
				disabled: true,
				onSelect: () => {},
			}];
	const name = t("nav.favorites", "Favorites");
	return (
		<div className={styles.favoritesBar} data-favorites>
			<ActionMenu
				items={items}
				label={name}
				align="left"
				triggerClassName={styles.favoritesTrigger}
				triggerIcon={<>
					<Star size={20} className={styles.icon} aria-hidden="true" />
					<MoreHorizontal size={16} className={styles.favoritesDots} aria-hidden="true" />
				</>}
				triggerLabel={name}
			/>
		</div>
	);
}
